import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { getDarkerColor } from '../helpers/colors';
import topicData from '../helpers/state/topic-data';

@customElement('tabs-component')
export class TopicMetadata extends LitElement {
    amplitude_callsite: string = 'Tabs Component';

    @property()
    options: string[] = [];

    @property()
    type: 'tabs' | 'pill' = 'tabs';

    @state()
    tabIndex: number = 0;

    private handleSelectTab(newIndex: number) {
        this.tabIndex = newIndex;
        this.dispatchEvent(
            new CustomEvent('on-select-tab', {
                detail: {
                    tabIndex: newIndex,
                },
            })
        );
    }

    private handleTabKeydown(e: KeyboardEvent, index: number) {
        const visibleIndices = this.options.map((opt, i) => opt !== '' ? i : -1).filter(i => i !== -1);
        if (visibleIndices.length === 0) return;
        const currentVisiblePos = visibleIndices.indexOf(index);
        if (currentVisiblePos === -1) return;
        let newVisiblePos = currentVisiblePos;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            newVisiblePos = (currentVisiblePos + 1) % visibleIndices.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            newVisiblePos = (currentVisiblePos - 1 + visibleIndices.length) % visibleIndices.length;
        } else if (e.key === 'Home') {
            e.preventDefault();
            newVisiblePos = 0;
        } else if (e.key === 'End') {
            e.preventDefault();
            newVisiblePos = visibleIndices.length - 1;
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleSelectTab(index);
            return;
        } else {
            return;
        }

        const newIndex = visibleIndices[newVisiblePos];
        this.handleSelectTab(newIndex);
        // Focus the newly selected tab
        void this.updateComplete.then(() => {
            const wrapper = this.shadowRoot?.querySelector('.tabs-wrapper, .pills-wrapper');
            const tabs = wrapper?.querySelectorAll('[role="tab"]');
            (tabs?.[newVisiblePos] as HTMLElement)?.focus();
        });
    }

    render() {
        return this.type === 'pill' ? this.renderPills() : this.renderTabs();
    }

    renderTabs() {
        const theme_color = getDarkerColor(topicData?.backgroundColor, topicData?.textColor);
        return html`
            <div class="tabs-wrapper" role="tablist">
                ${this.options.map((option, index) => option === '' ? html`` : html`
                    <button
                        role="tab"
                        aria-selected="${this.tabIndex === index}"
                        tabindex="${this.tabIndex === index ? 0 : -1}"
                        class="tab-btn ${this.tabIndex === index ? 'active' : ''}" style="${this.tabIndex === index ? `font-weight: bold; color: ${theme_color}; border-bottom: 2px solid ${theme_color};"` : ''}"
                        @click=${() => this.handleSelectTab(index)}
                        @keydown=${(e: KeyboardEvent) => this.handleTabKeydown(e, index)}
                    >${option}</button>
                `)}
            </div>
        `;
    }

    renderPills() {
        const theme_color = getDarkerColor(topicData?.backgroundColor, topicData?.textColor);
        return html`
            <div class="pills-wrapper" role="tablist">
                ${this.options.map((option, index) => ({ option, index })).filter(({ option }) => option !== '').map(({ option, index }) => {
                    return html`
                    <button
                        role="tab"
                        aria-selected="${this.tabIndex === index}"
                        tabindex="${this.tabIndex === index ? 0 : -1}"
                        class="pills-btn ${this.tabIndex === index ? 'active' : ''}" style="${this.tabIndex === index ? `font-weight: bold; background-color: ${theme_color}; color: white; border-radius:8px` : `background-color: var(--theme-border-color); color: ${theme_color};`}"
                        @click=${() => this.handleSelectTab(index)}
                        @keydown=${(e: KeyboardEvent) => this.handleTabKeydown(e, index)}
                    >${option}</button>
                `;})}
            </div>
        `;
    }

    static get styles() {
       return css`
            .tabs-wrapper {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                overflow: auto;
                flex-wrap: wrap;
                justify-content: center;
            }

            .pills-wrapper {
                display: flex;
                align-items: center;
                overflow: auto;
                flex-wrap: wrap;
                justify-content: center;
                background-color: var(--theme-border-color);
                border-radius: 8px;
            }

            .pills-btn {
                display: flex;
                flex: 1;
                justify-content: center;
                align-items: center;
                border: none;
                padding: 0.5rem;
                font-size: 0.75rem;
                background-color: var(--theme-white-color);
                color: var(--main-primary-color);
                text-align: center;
                text-decoration: none;
                white-space: nowrap;
                vertical-align: middle;
                cursor: pointer;
                -webkit-user-select: none;
                -moz-user-select: none;
                user-select: none;
                transition: all 0.2s ease-in-out;
                font-family: var(--main-font-family);
                font-weight: bold;
                color: var(--main-moon-color);
            }

            .pills-btn:focus-visible,
            .tab-btn:focus-visible {
                outline: 2px solid var(--main-primary-color, #005fcc);
                outline-offset: 2px;
            }

            .tab-btn {
                display: flex;
                justify-content: center;
                align-items: center;
                border: none;
                padding: 0.5rem;
                font-size: 0.75rem;
                background-color: var(--theme-white-color);
                color: var(--main-primary-color);
                text-align: center;
                text-decoration: none;
                white-space: nowrap;
                vertical-align: middle;
                cursor: pointer;
                -webkit-user-select: none;
                -moz-user-select: none;
                user-select: none;
                transition: all 0.2s ease-in-out;
                font-family: var(--main-font-family);
            }

            .tab-btn.active {
                font-weight: bold;
                color: var(--main-secondary-color);
                border-bottom: 2px solid var(--main-secondary-color);
            }
        `;

    }
}
